import { ipToInt } from "./ip";

export const splitSubnets = (cidr: string, subnetCount: number): string[] => {
  const [baseIp, prefixStr] = cidr.split("/");
  const prefix = parseInt(prefixStr, 10);

  if (isNaN(prefix) || prefix < 0 || prefix > 32) {
    throw new Error("Invalid CIDR prefix");
  }

  const bitsToBorrow = Math.log2(subnetCount);
  const newPrefix = prefix + bitsToBorrow;

  if (newPrefix > 32) {
    throw new Error("Cannot create that many subnets from the given network");
  }

  const intToIp = (num: number): string => {
    return [
      (num >>> 24) & 255,
      (num >>> 16) & 255,
      (num >>> 8) & 255,
      num & 255,
    ].join(".");
  };

  const networkAddress = ipToInt(baseIp) & (0xffffffff << (32 - prefix));
  const subnetSize = 1 << (32 - newPrefix);
  const subnets: string[] = [];

  for (let i = 0; i < subnetCount; i++) {
    const subnetAddress = networkAddress + i * subnetSize;
    subnets.push(`${intToIp(subnetAddress)}/${newPrefix.toFixed(0)}`);
  }

  return subnets;
};

export const intToIp = (int: number) => {
  return [24, 16, 8, 0].map((shift) => (int >> shift) & 255).join(".");
};

export const getBroadcastAddress = (cidr: string): string => {
  const [ip, prefixLength] = cidr.split("/");
  const prefix = parseInt(prefixLength, 10);
  const ipInt = ipToInt(ip);
  const subnetMask = (0xffffffff << (32 - prefix)) >>> 0;
  const networkAddress = ipInt & subnetMask;
  const broadcastAddress = networkAddress | (~subnetMask >>> 0);

  return intToIp(broadcastAddress);
};

export const getNetworkAddress = (cidr: string): string => {
  const [ip, prefixLength] = cidr.split("/");
  const prefix = parseInt(prefixLength, 10);
  const ipInt = ipToInt(ip);
  const subnetMask = (0xffffffff << (32 - prefix)) >>> 0;
  return intToIp(ipInt & subnetMask);
};

export const getHostAddress = (cidr: string, hostOffset: number): string => {
  const [ip, prefixLength] = cidr.split("/");
  const prefix = parseInt(prefixLength, 10);
  const ipInt = ipToInt(ip);
  const subnetMask = (0xffffffff << (32 - prefix)) >>> 0;
  const networkAddress = ipInt & subnetMask;
  const firstHost = networkAddress + 1;
  const maxOffset = (1 << (32 - prefix)) - 2;

  if (hostOffset < 0 || hostOffset >= maxOffset) {
    throw new Error(
      `Host offset out of range. Valid range: 0-${maxOffset - 1}`,
    );
  }

  return intToIp(firstHost + hostOffset);
};
